import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Textarea,
  Toast,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { LogoUpload } from '@/components/LogoUpload';

interface Setting {
  key: string;
  value: unknown;
  updated_at: string;
}

interface Readiness {
  status: string;
  checks: {
    database: string;
    redis: string;
    telephony: string;
    live_calls: number;
    mock_mode: { vicidial: boolean; telephony: boolean; ai: boolean };
  };
}

export default function PlatformSettings() {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const settings = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.get<{ data: Setting[] }>('/admin/settings'),
  });

  // Readiness lives outside the versioned API, so it is fetched directly.
  const readiness = useQuery({
    queryKey: ['readiness'],
    queryFn: async () => {
      const response = await fetch('/health/ready');
      return (await response.json()) as Readiness;
    },
    refetchInterval: 30_000,
  });

  const save = useMutation({
    mutationFn: () => {
      let parsed: unknown = value;
      try {
        parsed = JSON.parse(value);
      } catch {
        // Not JSON — store it as a plain string.
      }
      return api.put(`/admin/settings/${encodeURIComponent(key)}`, { value: parsed });
    },
    onSuccess: () => {
      setKey('');
      setValue('');
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setToast({ message: 'Setting saved', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not save'),
        tone: 'bad',
      }),
  });

  if (settings.isPending) return <Loading />;
  if (settings.error)
    return <ErrorState error={settings.error} retry={() => settings.refetch()} />;

  const checks = readiness.data?.checks;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Platform settings"
        description="Runtime health and platform-wide key/value settings."
      />

      <LogoUpload
        endpoint="/admin/branding/logo"
        label="Platform logo"
        description="Shown on the sign-in page and in the sidebar, for every client and every admin."
        invalidate={['admin-settings']}
      />

      <div className="mb-4" />

      <Card title="Health" className="mb-4">
        {checks ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <HealthBadge label="Database" state={checks.database} />
              <HealthBadge label="Redis" state={checks.redis} />
              <HealthBadge label="Telephony" state={checks.telephony} />
              <Badge tone="gray">{checks.live_calls} live calls</Badge>
            </div>

            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-ink-500">Mock modes</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(checks.mock_mode).map(([name, enabled]) => (
                  <Badge key={name} tone={enabled ? 'amber' : 'green'}>
                    {name}: {enabled ? 'mock' : 'live'}
                  </Badge>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
                Mock modes are set by environment variables, not here — turn them off one at a
                time after working through the checklists in{' '}
                <code className="font-mono">docs/VICIDIAL.md</code> and{' '}
                <code className="font-mono">docs/ASTERISK.md</code>.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-500">Health information unavailable.</p>
        )}
      </Card>

      <Card title="Settings" padded={false}>
        {settings.data.data.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-ink-500">
            No platform settings stored.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {settings.data.data.map((setting) => (
              <li key={setting.key} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-sm text-ink-900 dark:text-ink-50">
                    {setting.key}
                  </span>
                  <span className="text-xs text-ink-400">
                    {formatDateTime(setting.updated_at)}
                  </span>
                </div>
                <pre className="mt-1 overflow-x-auto rounded bg-sunken p-2 text-[11px] text-ink-600 dark:text-ink-300">
                  {JSON.stringify(setting.value, null, 2)}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PurgeCalls />
      <PurgeEverything />

      <Card title="Set a value" className="mt-4">
        <div className="space-y-3">
          <Field label="Key" required>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="feature.new_dashboard"
            />
          </Field>
          <Field label="Value" hint="JSON is parsed; anything else is stored as a string.">
            <Textarea rows={4} value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
          <Button disabled={!key} loading={save.isPending} onClick={() => save.mutate()}>
            Save setting
          </Button>
        </div>
      </Card>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

function HealthBadge({ label, state }: { label: string; state: string }) {
  const ok = state === 'ok';
  const degraded = state.startsWith('degraded');
  return (
    <Badge tone={ok ? 'green' : degraded ? 'amber' : 'red'}>
      {label}: {state}
    </Badge>
  );
}


interface PurgePreview {
  deleted: boolean;
  would_delete?: {
    calls: number;
    oldest: string | null;
    newest: string | null;
    clients: number;
    /** In a live state within the same bounds — deleted only if asked for. */
    live_calls?: number;
    /** How many of those this run will leave behind. */
    skipped_live?: number;
  };
  calls_deleted?: number;
  live_calls_ended?: number;
  skipped_live?: number;
  note?: string;
  everything?: boolean;
  /** The words the server will require, so the UI cannot ask for the wrong ones. */
  confirm_phrase_required?: string | null;
}

/**
 * Clearing old call history.
 *
 * Irreversible, so it counts before it deletes -- and the preview and the
 * deletion use the same query, because a preview computed differently is a
 * preview of something else.
 */
function PurgeCalls() {
  const [days, setDays] = useState(90);
  const [preview, setPreview] = useState<PurgePreview | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const run = useMutation({
    mutationFn: (confirm: boolean) =>
      api.post<PurgePreview>('/admin/purge-calls', {
        older_than_days: days,
        confirm,
      }),
    onSuccess: (result) => {
      if (result.deleted) {
        setPreview(null);
        setToast({
          message: `Deleted ${result.calls_deleted?.toLocaleString() ?? 0} call(s).`,
          tone: 'good',
        });
      } else {
        setPreview(result);
      }
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not purge'), tone: 'bad' }),
  });

  const count = preview?.would_delete?.calls ?? 0;

  return (
    <Card
      title="Clear old call history"
      description="Calls, transcripts, transfers and recordings older than the cutoff. Cannot be undone."
      className="mt-4"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Older than (days)">
          <Input
            type="number"
            min={1}
            max={3650}
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPreview(null);
            }}
          />
        </Field>
        <Button
          variant="secondary"
          loading={run.isPending && run.variables === false}
          onClick={() => run.mutate(false)}
        >
          Count what would go
        </Button>
      </div>

      {preview && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          {count === 0 ? (
            <p>Nothing is older than {days} days. There is nothing to delete.</p>
          ) : (
            <>
              <p className="font-medium">
                {count.toLocaleString()} call{count === 1 ? '' : 's'} across{' '}
                {preview.would_delete?.clients ?? 0} client(s) would be deleted
                permanently.
              </p>
              {preview.would_delete?.oldest && (
                <p className="mt-1 text-xs">
                  {formatDateTime(preview.would_delete.oldest)} to{' '}
                  {formatDateTime(preview.would_delete.newest ?? '')}
                </p>
              )}
              {/* The number that was missing. Without it the operator runs
                  the delete, is told a count, and finds rows still on the
                  Calls page with nothing anywhere explaining them. */}
              {(preview.would_delete?.skipped_live ?? 0) > 0 && (
                <p className="mt-2 font-medium text-amber-800 dark:text-amber-300">
                  {preview.would_delete?.skipped_live?.toLocaleString()} call(s)
                  in progress will be LEFT BEHIND — those are the ones that show
                  no disposition.
                </p>
              )}
              <div className="mt-3">
                <Button
                  variant="danger"
                  loading={run.isPending && run.variables === true}
                  onClick={() => run.mutate(true)}
                >
                  Delete {count.toLocaleString()} call{count === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
          {preview.note && <p className="mt-3 text-xs">{preview.note}</p>}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}

/**
 * Deleting all of it.
 *
 * A separate card from the cutoff one on purpose. They are different
 * intents -- one is housekeeping, the other empties the platform -- and
 * sharing a form would put the dangerous one a keystroke away from the
 * ordinary one.
 *
 * It counts first, then asks for the phrase to be typed. The phrase comes
 * back from the server rather than being written here twice: a confirmation
 * the UI and the API disagree about is one that always fails, and the fix
 * people reach for is to remove it.
 */
function PurgeEverything() {
  const [preview, setPreview] = useState<PurgePreview | null>(null);
  const [typed, setTyped] = useState('');
  const [includeLive, setIncludeLive] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const run = useMutation({
    mutationFn: (confirm: boolean) =>
      api.post<PurgePreview>('/admin/purge-calls', {
        everything: true,
        confirm,
        include_live: includeLive,
        confirm_phrase: confirm ? typed : undefined,
      }),
    onSuccess: (result) => {
      if (result.deleted) {
        setPreview(null);
        setTyped('');
        const ended = result.live_calls_ended ?? 0;
        const left = result.skipped_live ?? 0;
        setToast({
          // Says what happened, including what did not. The old message
          // reported a count and said nothing about the rows still on the
          // page afterwards, which is how "Delete ALL" came to look broken.
          message:
            `Deleted ${result.calls_deleted?.toLocaleString() ?? 0} call(s).` +
            (ended ? ` Ended ${ended} in progress first.` : '') +
            (left ? ` ${left} still in progress were left.` : ''),
          tone: 'good',
        });
      } else {
        setPreview(result);
      }
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not delete'), tone: 'bad' }),
  });

  const count = preview?.would_delete?.calls ?? 0;
  const phrase = preview?.confirm_phrase_required ?? 'DELETE ALL CALLS';
  const ready = typed.trim() === phrase;

  return (
    <Card
      title="Delete ALL call history"
      description="Every call, transcript, transfer and recording on the platform, at any age. Cannot be undone."
      className="mt-4"
    >
      <p className="text-sm text-ink-600 dark:text-ink-300">
        For clearing a test campaign before going live, or handing over a box.
        Invoices and usage totals are left alone — they are money, not history.
      </p>

      <label className="mt-3 flex items-start gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input
          type="checkbox"
          checked={includeLive}
          onChange={(e) => {
            setIncludeLive(e.target.checked);
            // The count changes with the switch, so a preview taken under the
            // old setting is no longer a preview of what will happen.
            setPreview(null);
            setTyped('');
          }}
          className="mt-0.5 rounded border-ink-300"
        />
        <span>
          <span className="font-medium">Include calls in progress</span>
          <span className="block text-xs text-ink-500">
            Calls in a live state are normally left alone, because one could be
            a conversation happening right now. On a box that has been under
            test they are almost always calls whose session died and which
            nothing will ever end — and they are the rows that stay on the page
            after a delete, showing no disposition. Each is ended and hung up
            properly before it is removed.
          </span>
        </span>
      </label>

      <div className="mt-3">
        <Button
          variant="secondary"
          loading={run.isPending && run.variables === false}
          onClick={() => run.mutate(false)}
        >
          Count everything
        </Button>
      </div>

      {preview && (
        <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/40">
          {count === 0 ? (
            <p>There is no call history to delete.</p>
          ) : (
            <>
              <p className="font-medium">
                {count.toLocaleString()} call{count === 1 ? '' : 's'} across{' '}
                {preview.would_delete?.clients ?? 0} client(s) — all of it.
              </p>
              {preview.would_delete?.oldest && (
                <p className="mt-1 text-xs">
                  {formatDateTime(preview.would_delete.oldest)} to{' '}
                  {formatDateTime(preview.would_delete.newest ?? '')}
                </p>
              )}
              {/* The number that was missing. Without it the operator runs
                  the delete, is told a count, and finds rows still on the
                  Calls page with nothing anywhere explaining them. */}
              {(preview.would_delete?.skipped_live ?? 0) > 0 && (
                <p className="mt-2 font-medium text-amber-800 dark:text-amber-300">
                  {preview.would_delete?.skipped_live?.toLocaleString()} call(s)
                  in progress will be LEFT BEHIND — those are the ones that show
                  no disposition. Tick the box above to end and remove them too.
                </p>
              )}
              {includeLive && (preview.would_delete?.live_calls ?? 0) > 0 && (
                <p className="mt-2 font-medium">
                  Includes {preview.would_delete?.live_calls?.toLocaleString()}{' '}
                  in progress, which will be hung up first.
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Field label={`Type ${phrase} to confirm`}>
                  <Input
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={phrase}
                    autoComplete="off"
                  />
                </Field>
                <Button
                  variant="danger"
                  disabled={!ready}
                  loading={run.isPending && run.variables === true}
                  onClick={() => run.mutate(true)}
                >
                  Delete all {count.toLocaleString()} call
                  {count === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
          {preview.note && <p className="mt-3 text-xs">{preview.note}</p>}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}
